// Admin reset. Resets the item to Pending; this is a loud-audit,
// exceptional action.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button, Textarea, FormField } from "@/design-system/components/ui";
import { ADMIN_UNDO_MIN_REASON_LENGTH } from "../admin/AdminUndoConfirmModal";

/** The server's limit for an admin reason (`CriteriaChecklistService.ValidateResetReason`). */
const MAX_REASON_LENGTH = 1000;

interface AdminResetModalProps {
  open: boolean;
  onClose: () => void;
  itemCode: string;
  onConfirm: (justification: string) => void | Promise<void>;
  pending?: boolean;
}

export function AdminResetModal({
  open,
  onClose,
  itemCode,
  onConfirm,
  pending = false,
}: AdminResetModalProps) {
  const [justification, setJustification] = useState("");
  // Mirrors the server: 5–1000 characters after trimming, like an admin undo reason.
  const length = justification.trim().length;
  const isValid = length >= ADMIN_UNDO_MIN_REASON_LENGTH && length <= MAX_REASON_LENGTH;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Admin reset ${itemCode}`}
      description="Resets this item to Pending. This action is recorded loudly in the audit log and should not be routine."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void onConfirm(justification)}
            disabled={!isValid || pending}
          >
            {pending ? "Resetting…" : "Reset to Pending"}
          </Button>
        </>
      }
    >
      <FormField
        label="Justification"
        required
        helperText={`${ADMIN_UNDO_MIN_REASON_LENGTH}–${MAX_REASON_LENGTH} characters, recorded in the audit log.`}
      >
        <Textarea
          rows={5}
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          placeholder="Why is this exceptional reset necessary?"
        />
      </FormField>
    </Modal>
  );
}
